import { useFormStore } from "@/contexts/paymentFlowContext";
import useCurrencyStore, { Currencies } from "@/store/currencyStore";
import { useLocation, useNavigate } from "@tanstack/react-router";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import config from "@/config/config";
import api from "@/api/api-client";
import dayjs from "dayjs";
import { v4 } from "uuid";
import { SpecialDonation } from "@/types/donation";
import { defaultCurrencies, getConversionApproximation, preciselyRound } from "@/utils/currency";
import { getClientIp } from "@/api/client-ip.ts";

const formatDate = (date: Date | null) => {
  return dayjs(date).format("YYYY-MM-DD");
};

export const PaymentProcessing = () => {
  const { search } = useLocation();
  const navigate = useNavigate();
  const { currency, conversionRate } = useCurrencyStore();
  const {
    inputAmount,
    merchantId,
    selectedProgram,
    finalMonth,
    cardCountry,
    donationType,
    quantity,
    livestock,
    item,
    deliveryAddress,
    deliveryCity,
    deliveryPhoneNumber,
    deliveryDate,
    slot,
    isDelivery,
    isSpecial,
    isItemization,
    agreementId,
    isDedicated,
    honoreeEmail,
    honoreeName,
    dedicationMessage,
    isChallenge,
    selectedChallenge,
    isSpecialFormInputAmountInUSD,
  } = useFormStore();
  const { i18n, t } = useTranslation();
  const paymentAPIRoute = config.baseURL + "/payment/transaction/";
  const redirectAPIRoute = config.baseURL + "/payment/callback/";

  const [ipAddress, setIpAddress] = useState("");

  const [isIpFetched, setIsIpFetched] = useState(false);
  const recurring_mode = "UNSCHEDULED";

  const conversionApproximation = useMemo(
    () => getConversionApproximation(inputAmount, "toUSD"),
    [currency, conversionRate, inputAmount],
  );

  const getIsSpecialDonation = useCallback(() => {
    if (isSpecial && isDelivery) {
      return {
        quantity: quantity,
        item_id: null,
        livestock_id: livestock,
        isDelivery: !!isDelivery,
        delivery_address: deliveryAddress,
        delivery_city: deliveryCity,
        phonenumber: deliveryPhoneNumber,
        delivery_date: formatDate(deliveryDate),
        delivery_slot_id: slot ? slot.id : null,
      };
    } else if (isSpecial && !isDelivery) {
      return {
        quantity: quantity,
        item_id: null,
        livestock_id: livestock,
        isDelivery: !!isDelivery,
        delivery_address: null,
        delivery_city: null,
        phonenumber: null,
        delivery_date: null,
        delivery_slot_id: null,
      };
    } else if (isItemization) {
      return {
        quantity: quantity,
        item_id: item,
        livestock_id: null,
        isDelivery: false,
        delivery_address: null,
        delivery_city: null,
        phonenumber: null,
        delivery_date: null,
        delivery_slot_id: null,
      };
    } else {
      return undefined;
    }
  }, [isSpecial, isDelivery]);

  useEffect(() => {
    const fetchIPAndCountry = async () => {
      try {
        const { ip } = await getClientIp();
        setIpAddress(ip);
        setIsIpFetched(true);
      } catch (error) {
        setIsIpFetched(true);
      }
    };

    fetchIPAndCountry();
  }, []);

  useEffect(() => {
    if (!isIpFetched || !inputAmount) {
      return;
    }
    const { phoneNumber, email, fullName } = JSON.parse(localStorage.getItem("user-data") ?? "{}");

    // Determine the currency to use for payment processing
    // If the selected currency is not supported, default to USD
    // Supported currencies are defined in defaultCurrencies array
    // (currently only EGP and USD)
    const paymentCurrency = defaultCurrencies.includes(currency) ? currency : Currencies.USD;

    // Determine the base amount to use for payment processing
    // Three scenarios:
    // 1. Currency is already supported (EGP/USD) - use inputAmount directly
    // 2. Special donation with USD input - use inputAmount as it's already in USD
    //    (for special donations, inputAmount represents the USD value, not a third currency)
    // 3. Third currency (needs conversion) - use converted approximation to USD
    const baseAmount =
      defaultCurrencies.includes(currency) || (isSpecialFormInputAmountInUSD && isSpecial)
        ? inputAmount
        : Number(conversionApproximation);

    // Round to nearest positive integer for payment processor
    const roundedPaymentAmount = preciselyRound(baseAmount, 0);

    // Convert to cents for payment processor (multiply by 100 )
    const paymentAmountInCents = roundedPaymentAmount * 100;

    const payload: Record<
      string,
      string | number | boolean | Date | SpecialDonation | undefined | null
    > = {
      merchant_identifier: config.merchantIdAPS,
      payment_session_id: search.payment_session_id,
      currency: paymentCurrency,
      merchant_reference: merchantId,
      access_code: config.merchantAccessCodeAPS,
      language: i18n.language,
      command: "PURCHASE",
      amount: paymentAmountInCents,
      phone_number: phoneNumber || "-",
      customer_name: fullName,
      customer_email: email,
      client_ip: ipAddress,
      customer_ip: ipAddress,
      program_id: selectedProgram!.id,
      return_url: redirectAPIRoute,
      card_country: cardCountry,
      donation_type: donationType ?? null,
      agreement_id: agreementId ?? v4().replaceAll("-", "").substring(0, 15),
      special_donation: getIsSpecialDonation(),
      isDedicated: isDedicated,
      honoree_name: honoreeName,
      honoree_email: honoreeEmail,
      dedication_message: dedicationMessage,
      recurring_mode: recurring_mode,
      recurring_transactions_count: 1,
    };
    if (finalMonth) {
      payload.recurring_expiry_date = formatDate(finalMonth);
      payload.recurring_transactions_count = dayjs(finalMonth).diff(new Date(), "month");
    }
    if (search.payment_session_id) {
      api.post(paymentAPIRoute, payload).then((res) => {
        if (res.data["3ds_url"]) {
          window.location.href = res.data["3ds_url"];
        } else if (isChallenge) {
          navigate({
            to: `/challenge-checkout/${selectedChallenge?.id}`,
            search: { response_code: 400 },
          });
        } else {
          navigate({
            to: `/gratitude/${selectedProgram?.id}`,
            search: { response_code: 400 },
          });
        }
      });
    } else if (isChallenge) {
      navigate({
        to: `/challenge-checkout/${selectedChallenge?.id}`,
        search,
      });
    } else {
      navigate({ to: `/gratitude/${selectedProgram?.id}`, search });
    }
  }, [ipAddress]);

  return (
    <div className="fixed z-50 flex h-screen w-screen flex-col items-center justify-center bg-white">
      <div role="status">
        <svg
          className="h-8 w-8 animate-spin text-gray-200 dark:text-gray-600"
          viewBox="0 0 100 101"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z"
            fill="currentColor"
          />
          <path
            d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z"
            fill="#D5F476"
          />
        </svg>
        <span className="sr-only">Loading...</span>
      </div>
      <p className="text-2xl">{t("processing")}</p>
    </div>
  );
};
